import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  ScrollView,
  Dimensions,
  Animated,
  Easing,
  StyleSheet,
  Platform,
} from "react-native";
import { Toast } from "react-native-toast-notifications";
import AsyncStorage from "@react-native-async-storage/async-storage";
import secureStorage from "../../utils/secureStorage";
import customAxios from "../../utils/customAxios";
import i18n from "../../locales/i18n";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { deactivateDeviceToken } from "../../lib/fcmNotifications";
import { notificationSocketService } from "../../lib/notificationSocket";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { appActions } from "../../store/slices/appSlice";
import { authActions } from "../../store/slices/authSlice";

// Import SVG components with proper sizing
import DeptIcon from "../../assets/drawerIcons/dept.svg";
import StockIcon from "../../assets/drawerIcons/stock.svg";
import BillsIcon from "../../assets/drawerIcons/info.svg";
import LanguagesIcon from "../../assets/drawerIcons/languages.svg";
import LogoutIcon from "../../assets/drawerIcons/logout.svg";
import NotificationIcon from "../../assets/drawerIcons/notifications.svg";
import SettingsIcon from "../../assets/drawerIcons/settings.svg";
import DistributorIcon from "../../assets/drawerIcons/distributor.svg";

const DRAWER_WIDTH = Dimensions.get("window").width * 0.8;

const DrawerMenu = () => {
  const { isDrawerOpen, locale } = useSelector((state: RootState) => state.app);
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [showLanguageOptions, setShowLanguageOptions] = useState(false);
  const animatedValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (isDrawerOpen) {
      Animated.spring(animatedValue, {
        toValue: 1,
        damping: 20,
        stiffness: 150,
        mass: 0.8,
        useNativeDriver: Platform.OS !== "web",
      }).start();
    } else {
      Animated.timing(animatedValue, {
        toValue: 0,
        duration: 250,
        easing: Easing.in(Easing.ease),
        useNativeDriver: Platform.OS !== "web",
      }).start();
    }
  }, [isDrawerOpen]);

  const drawerTranslateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [-DRAWER_WIDTH, 0],
  });

  const overlayOpacity = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  const handleLanguageChange = (selectedLocale: string) => {
    // setLocale now handles both i18n update and AsyncStorage persistence
    dispatch(appActions.setLocale(selectedLocale));
    setShowLanguageOptions(false);
  };

  // Helper function to render SVG icons with consistent sizing
  const renderIcon = (
    IconComponent: React.ComponentType<{
      width: number;
      height: number;
      fill: string;
    }>,
  ) => {
    return (
      <View style={styles.iconContainer}>
        <IconComponent
          width={24}
          height={24}
          fill="#374151" // Set a default fill color
        />
      </View>
    );
  };

  interface MenuItem {
    id: number;
    title: string;
    icon: React.ReactNode;
    route: string;
    isLogout?: boolean;
  }
  const menuItems: MenuItem[] = [
    {
      id: 1,
      title: i18n.t("stock"),
      icon: renderIcon(StockIcon),
      route: "/(drawer)/stock",
    },
    {
      id: 2,
      title: i18n.t("start"),
      icon: renderIcon(DistributorIcon),
      route: "/(drawer)/start",
    },
    {
      id: 3,
      title: i18n.t("credit"),
      icon: renderIcon(DeptIcon),
      route: "/(drawer)/dettes",
    },
    {
      id: 4,
      title: i18n.t("bills"),
      icon: renderIcon(BillsIcon),
      route: "/(drawer)/bills",
    },
    {
      id: 5,
      title: i18n.t("orders"),
      icon: (
        <View style={styles.iconContainer}>
          <Ionicons name="receipt-outline" size={24} color="#374151" />
        </View>
      ),
      route: "/(drawer)/orders",
    },
    {
      id: 6,
      title: i18n.t("settings"),
      icon: renderIcon(SettingsIcon),
      route: "/(drawer)/params",
    },
    {
      id: 7,
      title: i18n.t("limits"),
      icon: (
        <View style={styles.iconContainer}>
          <Ionicons name="bar-chart-outline" size={24} color="#374151" />
        </View>
      ),
      route: "/(drawer)/params/limits",
    },
    {
      id: 8,
      title: i18n.t("scanner"),
      icon: renderIcon(NotificationIcon),
      route: "/(drawer)/scanner",
    },
    {
      id: 9,
      title: i18n.t("logout"),
      icon: renderIcon(LogoutIcon),
      route: "/logout",
      isLogout: true,
    },
  ];

  const handleMenuItemPress = async (item: MenuItem | { route: string }) => {
    dispatch(appActions.toggleDrawer());

    setTimeout(async () => {
      if ((item as MenuItem).isLogout) {
        try {
          const refreshToken = await secureStorage.getItemAsync("refreshToken");

          if (refreshToken) {
            const res = await customAxios.post(
              `/mobile/logout`,
              { refreshToken },
              { withCredentials: true },
            );

            Toast.show(res.data.message, {
              type: "success",
            });
          }

          dispatch(authActions.setUser(null));
          router.replace("/(routes)/(auth)/login");
        } catch (error) {
          Toast.show(i18n.t("logoutFailed"), {
            type: "danger",
          });
        } finally {
          // Deactivate push token & disconnect WebSocket
          try {
            await deactivateDeviceToken();
          } catch {}
          notificationSocketService.disconnect();
          await AsyncStorage.removeItem("accessToken");
          await secureStorage.deleteItemAsync("refreshToken");
        }
      } else {
        router.push(item.route as any);
      }
    }, 50);
  };

  const handleOverlayPress = () => {
    dispatch(appActions.toggleDrawer());
  };

  const handleClosePress = () => {
    dispatch(appActions.toggleDrawer());
  };

  // Keep rendering during close animation
  const [shouldRender, setShouldRender] = React.useState(false);

  useEffect(() => {
    if (isDrawerOpen) {
      setShouldRender(true);
    } else {
      // Wait for close animation to finish before unmounting
      const timeout = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isDrawerOpen]);

  if (!shouldRender) {
    return null;
  }

  return (
    <View
      style={styles.container}
      pointerEvents={isDrawerOpen ? "auto" : "none"}
    >
      <Animated.View style={[styles.overlay, { opacity: overlayOpacity }]}>
        <TouchableOpacity
          style={StyleSheet.absoluteFill}
          onPress={handleOverlayPress}
        />
      </Animated.View>

      <Animated.View
        style={[
          styles.drawerContainer,
          { transform: [{ translateX: drawerTranslateX }] },
        ]}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerTopRow}>
            <TouchableOpacity onPress={handleClosePress}>
              <Ionicons name="close" size={28} color="white" />
            </TouchableOpacity>
            <Text style={styles.headerTitle}>{i18n.t("settings")}</Text>
            <View style={{ width: 28 }} />
          </View>

          <TouchableOpacity
            onPress={() =>
              handleMenuItemPress({ route: "/(drawer)/params/profile" })
            }
            style={styles.profileSection}
          >
            <View style={styles.profileInner}>
              {(user as any)?.photo ? (
                <Image
                  source={{
                    uri: (user as any).photo.startsWith("http")
                      ? (user as any).photo
                      : `https://api.khdame.com${(user as any).photo}`,
                  }}
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 25,
                    borderWidth: 2,
                    borderColor: "rgba(255,255,255,0.3)",
                  }}
                  resizeMode="cover"
                />
              ) : (
                <Ionicons name="person-circle" size={50} color="white" />
              )}
              <View style={styles.profileTextContainer}>
                <Text style={styles.profileUsername}>
                  {(user as any)?.username || i18n.t("username")}
                </Text>
                <Text style={styles.profileViewText}>
                  {i18n.t("viewProfile")}
                </Text>
              </View>
              <Ionicons
                name="chevron-forward"
                size={22}
                color="rgba(255,255,255,0.8)"
              />
            </View>
          </TouchableOpacity>
        </View>

        {/* Menu Items */}
        <ScrollView
          style={styles.menuScrollView}
          contentContainerStyle={styles.menuContentContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.menuItemsList}>
            {menuItems.map((item) => (
              <TouchableOpacity
                key={item.id}
                style={styles.menuItem}
                onPress={() => handleMenuItemPress(item)}
                activeOpacity={0.7}
              >
                {item.icon}

                <Text
                  style={[
                    styles.menuItemTitle,
                    { textAlign: locale === "ar" ? "right" : "left" },
                  ]}
                >
                  {item.title}
                </Text>
                <Ionicons name="chevron-forward" size={18} color="#9CA3AF" />
              </TouchableOpacity>
            ))}

            <View style={styles.divider} />

            <View style={styles.languageSection} className="mb-32">
              <View
                style={{
                  flexDirection: locale === "ar" ? "row-reverse" : "row",
                  alignItems: "center",
                  marginBottom: 12,
                  marginLeft: 12,
                  direction: "ltr",
                }}
              >
                <View style={styles.iconContainer}>
                  <LanguagesIcon width={24} height={24} fill="#374151" />
                </View>
                <Text style={styles.languageTitle}>{i18n.t("language")}</Text>
              </View>
              <TouchableOpacity
                style={styles.languageSelector}
                onPress={() => setShowLanguageOptions(!showLanguageOptions)}
              >
                <Text style={styles.languageSelectorText}>
                  {locale === "ar"
                    ? i18n.t("arabicLang")
                    : locale === "fr"
                      ? i18n.t("frenchLang")
                      : i18n.t("englishLang")}
                </Text>
                <Ionicons
                  name={showLanguageOptions ? "chevron-up" : "chevron-down"}
                  size={20}
                  color="#374151"
                />
              </TouchableOpacity>

              {showLanguageOptions && (
                <View style={styles.languageOptionsContainer}>
                  <TouchableOpacity
                    style={styles.languageOption}
                    onPress={() => handleLanguageChange("ar")}
                  >
                    <View style={styles.languageOptionTextContainer}>
                      <Text style={styles.languageOptionText}>
                        {i18n.t("arabicLang")}
                      </Text>
                      <Text style={styles.languageOptionSubText}>Arabic</Text>
                    </View>
                    {locale === "ar" && (
                      <Ionicons name="checkmark" size={20} color="#10B981" />
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.languageOption, { borderBottomWidth: 0 }]}
                    onPress={() => handleLanguageChange("fr")}
                  >
                    <View style={styles.languageOptionTextContainer}>
                      <Text style={styles.languageOptionText}>
                        {i18n.t("frenchLang")}
                      </Text>
                      <Text style={styles.languageOptionSubText}>French</Text>
                    </View>
                    {locale === "fr" && (
                      <Ionicons name="checkmark" size={20} color="#10B981" />
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.languageOption, { borderBottomWidth: 0 }]}
                    onPress={() => handleLanguageChange("en")}
                  >
                    <View style={styles.languageOptionTextContainer}>
                      <Text style={styles.languageOptionText}>
                        {i18n.t("englishLang")}
                      </Text>
                      <Text style={styles.languageOptionSubText}>English</Text>
                    </View>
                    {locale === "en" && (
                      <Ionicons name="checkmark" size={20} color="#10B981" />
                    )}
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  drawerContainer: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    width: DRAWER_WIDTH,
    backgroundColor: "white",
    shadowColor: "#000",
    shadowOffset: { width: 5, height: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 10,
    elevation: 20,
  },
  header: {
    backgroundColor: "rgba(19, 121, 162, 1)",
    paddingTop: Platform.OS === "ios" ? 50 : 40,
    paddingBottom: 20,
  },
  headerTopRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "white",
  },
  profileSection: {
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  profileInner: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: 12,
    padding: 15,
  },
  profileTextContainer: {
    flex: 1,
    marginLeft: 15,
  },
  profileUsername: {
    color: "white",
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 2,
  },
  profileViewText: {
    color: "rgba(255,255,255,0.8)",
    fontSize: 14,
  },
  menuScrollView: {
    flex: 1,
    backgroundColor: "white",
  },
  menuContentContainer: {
    paddingVertical: 20,
  },
  menuItemsList: {
    paddingHorizontal: 10,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 15,
    paddingHorizontal: 20,
    marginVertical: 2,
    marginHorizontal: 8,
    borderRadius: 12,
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  iconContainer: {
    width: 28,
    height: 28,
    marginRight: 15,
    justifyContent: "center",
    alignItems: "center",
  },
  menuItemTitle: {
    fontSize: 16,
    color: "#374151",
    fontWeight: "500",
    flex: 1,
  },
  divider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginVertical: 20,
    marginHorizontal: 20,
  },
  languageSection: {
    paddingHorizontal: 8,
  },
  languageTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#374151",
    marginBottom: 12,
    marginLeft: 12,
  },
  languageSelector: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundColor: "#F3F4F6",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    marginHorizontal: 8,
  },
  languageSelectorText: {
    color: "#1F2937",
    fontWeight: "500",
    fontSize: 16,
  },
  languageOptionsContainer: {
    marginTop: 8,
    marginHorizontal: 8,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 12,
    overflow: "hidden",
  },
  languageOption: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  languageOptionTextContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  languageOptionText: {
    color: "#1F2937",
    fontWeight: "500",
    marginRight: 12,
    fontSize: 16,
  },
  languageOptionSubText: {
    color: "#6B7280",
    fontSize: 14,
  },
});

export default DrawerMenu;
